import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import { FORM_ERROR } from 'final-form';
import { Select, TextField, Switches } from 'mui-rff';
import { Form } from 'react-final-form';
import ProdConfirmationDialog from '@components/ProdConfirmationDialog';

import { breadcrumbsActions } from '@store/breadcrumbs';
import { usePermissions } from '@store/auth';
import { showSuccessMessage } from '@store/dashboard';

import ApplicationPageTemplate from '@dashboardComponents/ApplicationPageTemplate/ApplicationPageTemplate';
import { getTabs, selectTabList } from '../../store/payment-options-tabs';
import {
  addNewMethod,
  getMethodDetails,
  getPaymentMethods,
  paymentOptionsMethodsActions,
  selectMethodList,
  selectSelectedMethod,
  updateMethod,
} from '../../store/payment-options-methods';
import {
  addPaymentMethod,
  editPaymentMethod,
  METHOD_LIST_ROUTE,
} from '../../payment-options.breadcrumbs';

import FormSaveAndSubmit from '../../../../components/FormSaveAndSubmit';
import { Typography } from '../../../../components/styledComponents';

const initForm = {
  methodStatus: true,
  methodCode: '',
  methodName: '',
  logoUrl: '',
  parentMethodDetails: {},
  tabDetails: {},
  propertiesSendToUi: false,
};

export default function MethodForm() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const tabs = useSelector(selectTabList);
  const methods = useSelector(selectMethodList);
  const selectedMethod = useSelector(selectSelectedMethod);

  const { id } = useParams();

  const permissions = usePermissions();
  const canEdit = permissions?.canEditPaymentOptions;

  // A view-only user can still open an existing record, but must not reach the
  // create form directly via its URL.
  useEffect(() => {
    if (id === undefined && !permissions?.canEditPaymentOptions) {
      navigate(METHOD_LIST_ROUTE);
    }
  }, [id, navigate, permissions]);

  const [isFetchingData, setIsFetchingData] = useState(true);
  const [formValue, setFormValue] = useState(initForm);
  const [formSuccessMsg, setFormSuccessMsg] = useState('');
  const [formErrorMsg, setFormErrorMsg] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState({ show: false });

  useEffect(() => {
    setIsFetchingData(true);
    dispatch(breadcrumbsActions.showMenu(true));

    if (id !== undefined) {
      dispatch(breadcrumbsActions.setBreadcrumbs(editPaymentMethod(id)));
      Promise.all([
        dispatch(getTabs()),
        dispatch(getPaymentMethods()),
        dispatch(getMethodDetails(id)),
      ]).then(() => setIsFetchingData(false));
    } else {
      dispatch(breadcrumbsActions.setBreadcrumbs(addPaymentMethod));
      Promise.all([
        dispatch(getTabs()),
        dispatch(getPaymentMethods()),
        dispatch(paymentOptionsMethodsActions.resetSelectedMethod()),
      ]).then(() => setIsFetchingData(false));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (id !== undefined) {
      setFormValue(selectedMethod);
    } else {
      setFormValue(initForm);
    }
  }, [selectedMethod, id]);

  const onSubmit = (values) => {
    setFormSuccessMsg('');
    setFormErrorMsg('');
    setSubmitLoading(true);

    const payload = {
      id,
      methodStatus: values.methodStatus,
      code: values.methodCode,
      name: values.methodName,
      logoUrl: values.logoUrl,
      methodTabId: values.tabDetails?.tabId,
      methodParentId: values.parentMethodDetails?.methodId,
      propertiesSendToUi: values.propertiesSendToUi,
    };

    return dispatch(
      id !== undefined ? updateMethod(payload) : addNewMethod(payload),
    )
      .then((data) => {
        if (data.payload && data.payload.status === 'SUCCESS') {
          if (id === undefined) {
            dispatch(
              showSuccessMessage(
                `Method ${id !== undefined ? 'updated' : 'added'} successfully`,
              ),
            );
            navigate(METHOD_LIST_ROUTE);
          } else {
            setFormSuccessMsg('Method has been updated successfully');
            setFormValue(values); // needed to disable buttons after successful submit
          }
        } else {
          setFormErrorMsg(data.payload.message);
        }
      })
      .finally(() => {
        setSubmitLoading(false);
      });
  };

  const validate = (values) => {
    if (!values.methodName) return { [FORM_ERROR]: 'Method Name is required' };
    if (!values.methodCode) return { [FORM_ERROR]: 'Method Code is required' };
    if (!values.logoUrl) return { [FORM_ERROR]: 'Logo Url is required' };
    if (!values.tabDetails.tabId) return { [FORM_ERROR]: 'Tab is required' };
    return {};
  };

  const handlePreSubmit = (e) => {
    e.preventDefault();
    setOpenDialog({ show: true }); // open the confirmation dialog if prod
  };

  return (
    <ApplicationPageTemplate
      title={id !== undefined ? selectedMethod.methodName : 'New Method'}
      pageReady={!isFetchingData}>
      {!isFetchingData && (
        <Form
          onSubmit={onSubmit}
          initialValues={formValue}
          validate={validate}
          render={({ handleSubmit, values, pristine, invalid, form }) => (
            <form
              onSubmit={(e) =>
                process.env.REACT_APP_ENV !== 'production'
                  ? handleSubmit(e)
                  : handlePreSubmit(e)
              }
              noValidate>
              <ProdConfirmationDialog
                openDialog={openDialog}
                yesHandler={handleSubmit}
              />
              <Grid
                container
                spacing={1}
                style={{
                  alignContent: 'center',
                  justifyContent: 'space-between',
                }}>
                <Grid
                  item
                  xs={12}
                  md={8}
                  style={{ flex: 2 }}>
                  {formSuccessMsg && (
                    <Alert
                      onClose={() => {
                        setFormSuccessMsg('');
                      }}
                      severity="success">
                      {formSuccessMsg}
                    </Alert>
                  )}
                  {formErrorMsg && (
                    <Alert
                      onClose={() => {
                        setFormErrorMsg('');
                      }}
                      severity="error">
                      {formErrorMsg}
                    </Alert>
                  )}
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={4}
                  style={{ display: 'flex' }}>
                  <FormSaveAndSubmit
                    id={id}
                    resetDisabled={pristine}
                    submitDisabled={pristine || invalid || !canEdit}
                    submitLoading={submitLoading}
                    handleReset={() => {
                      setFormSuccessMsg('');
                      setFormErrorMsg('');
                      form.reset();
                    }}
                  />
                </Grid>
              </Grid>
              <Box
                style={{
                  marginTop: '20px',
                  padding: '32px',
                  border: '1px solid #c4c4c4',
                  borderRadius: '4px',
                }}>
                <Typography
                  className="form-header"
                  variant="formHeader">
                  Details
                </Typography>
                <Grid
                  container
                  justifyContent="space-between"
                  alignItems="flex-start"
                  spacing={2}>
                  <Grid
                    item
                    xs={12}
                    md={6}>
                    <TextField
                      required
                      aria-label="method-name"
                      name="methodName"
                      variant="outlined"
                      label="Name"
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}>
                    <TextField
                      required
                      aria-label="method-code"
                      name="methodCode"
                      variant="outlined"
                      label="Code"
                      helperText="Format: tabCode-methodShortCode. Sample: cc-visa"
                      disabled={id !== undefined}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={7}
                    md={10}>
                    <TextField
                      required
                      name="logoUrl"
                      label="Logo URL"
                      variant="outlined"
                    />
                  </Grid>
                  <Grid
                    item
                    xs={5}
                    md={2}>
                    <Box
                      style={{
                        boxSizing: 'border-box',
                        border: '1px solid #c4c4c4',
                        borderRadius: '4px',
                        height: '56px',
                        maxHeight: '56px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '2px',
                      }}>
                      {values.logoUrl ? (
                        <img
                          style={{ height: '100%', maxWidth: '100%' }}
                          src={values.logoUrl}
                          alt="Source link preview"
                        />
                      ) : (
                        <Skeleton
                          variant="rectangular"
                          sx={{
                            width: '100%',
                            height: '100%',
                          }}
                        />
                      )}
                    </Box>
                  </Grid>
                  <Grid
                    item
                    xs={4}
                    md={3}>
                    <Switches
                      name="methodStatus"
                      required
                      data={{ label: 'Enabled', value: values.methodStatus }}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={4}
                    md={10}>
                    <Switches
                      name="propertiesSendToUi"
                      required
                      data={{
                        label: 'Send public method properties to UI',
                        value: values.properiesSendToUi,
                      }}
                    />
                  </Grid>
                </Grid>
                <Typography
                  className="form-header"
                  variant="formHeader">
                  Configuration
                </Typography>
                <Grid
                  container
                  spacing={4}>
                  <Grid
                    item
                    xs={6}>
                    <Select
                      required
                      name="tabDetails.tabId"
                      label="Tab"
                      helperText="Method group"
                      variant="outlined">
                      {tabs.map((item) => (
                        <MenuItem
                          key={item.id}
                          value={item.id}>
                          {`${item.name} (${item.code})`}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                  <Grid
                    item
                    xs={6}>
                    <Select
                      name="parentMethodDetails.methodId"
                      label="Parent Method"
                      helperText="Usually used for direct debit methods. Methods under the same parent will be shown in a drop-down list"
                      variant="outlined">
                      <MenuItem
                        // eslint-disable-next-line react/no-array-index-key
                        key={-1}
                        value={undefined}>
                        (none)
                      </MenuItem>
                      {methods.map((item, i) => (
                        <MenuItem
                          // eslint-disable-next-line react/no-array-index-key
                          key={i}
                          value={item.methodId}>
                          {`${item.methodName} (${item.methodCode})`}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                </Grid>
              </Box>
            </form>
          )}
        />
      )}
    </ApplicationPageTemplate>
  );
}
